import { DownloadOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { useRequest } from 'ahooks';
import { message } from 'antd';
import { FileIcon } from 'components/FileIcon';
import { useMemo } from 'react';
import { getMercadoAttachment } from 'services/api/conversation';
import { AttachmentImage } from './AttachmentImage';
import { openExternalURL } from 'utils/openExternalURL';

const isImage = (ext: string) => ['.jpg', '.jpeg', '.png'].includes(ext.toLowerCase());

export interface AttachmentListItemProps {
  storeId: Conversation.Conversation['storeId'];
  msgId: Conversation.Message['msgId'];
  data: Conversation.MsgAttachmentInfo;
}

export const AttachmentListItem = (props: AttachmentListItemProps) => {
  const {
    storeId,
    msgId,
    data: { filename, originalFilename },
  } = props;
  const { t } = useTranslate();

  const { loading, runAsync } = useRequest(
    () => {
      return getMercadoAttachment(storeId, filename, msgId);
    },
    {
      manual: true,
      cacheKey: `${storeId}_${filename}`,
    },
  );

  const { name, ext } = useMemo(() => {
    const dotIndex = originalFilename.lastIndexOf('.');
    const name = originalFilename.substring(0, dotIndex);
    const ext = originalFilename.substring(dotIndex, originalFilename.length);

    return {
      name,
      ext,
    };
  }, [originalFilename]);

  if (isImage(ext)) {
    return <AttachmentImage getURL={runAsync} data={props.data} />;
  }

  return (
    <div
      className="group relative w-14 h-14 flex flex-col justify-center items-center bg-[var(--fett-bg-info-tip)] gap-0.5"
      title={originalFilename}
    >
      <FileIcon className="text-[22px]" ext={ext} />
      <span className="w-10 text-xs text-content-200 text-center overflow-hidden text-ellipsis whitespace-nowrap">
        {name}
      </span>
      <div
        className="hidden group-hover:flex items-center justify-center absolute top-0 right-0 bottom-0 left-0 bg-[#000000] bg-opacity-50 cursor-pointer"
        onClick={async () => {
          try {
            if (loading) return;
            const { url } = await runAsync();
            openExternalURL(url, '__blank');
          } catch (err) {
            message.error(t('common.feedback.operationFailed'));
          }
        }}
      >
        <DownloadOutlined className="!text-fill-0" />
      </div>
    </div>
  );
};
